import { Injectable, OnInit } from '@angular/core';
import { HTTPService } from '../../shared/services/http.service';
import { ClientService } from '../../shared/services/client.service';
import { ManagedClient } from '../../shared/models/managed-client';
import { AssessmentService } from "../../shared/services/assessment.service";
import {Observable} from 'rxjs/Observable';
import {BehaviorSubject} from 'rxjs/BehaviorSubject';
import { User } from 'app/shared/models/user';
import { Role } from 'app/shared/models/role';
import { AlertService } from 'app/shared/services/alert.service';
import { AmplifyService } from 'aws-amplify-angular';
import { DacConfig } from 'app/shared/services/dac-config.service';
import { SubscriptionHydratorService } from 'app/shared/services/subscription-hydrator.service';
import { Subscription } from 'app/shared/models/subscription';

class LoginStateNotification {
  role: Role
  isLoggedIn: boolean;
  clientId: string;
  user:User;
}

@Injectable()
export class AuthService implements OnInit {

  private _isLoggedIn:boolean;
  private _clientId:string;
  private _client:ManagedClient;
  private _user:User;
  private _isSystemUser:boolean;
  private _loginStateSubject:BehaviorSubject<any>;
  private _inAuthentication:boolean;

  constructor(
    private _dac$http:HTTPService
    ,private _clientService:ClientService
    // TODO: stephen need to introduce a caching service that we can clear the caching service
    // when we logout to reset any current state.  For expediency we are just including this
    // service to reset, but this is bad cohesion.
    // TODO: stephen another option would be to use an event system and have all the services listen to the logout event.
    // this is more difficult to reason about though...
    ,private assessmentService:AssessmentService
    ,private alertService:AlertService
    ,private amplifyService: AmplifyService
    ,private subHydrator:SubscriptionHydratorService
    ,private dacConfig:DacConfig
  ) {
    this._isSystemUser = false;
    // TODO: stephen figure out why we can't put the sessionStorage logic in ngOnInit
    let roleId = +sessionStorage.getItem("roleId");
    let clientId = sessionStorage.getItem("clientId");
    let userId = sessionStorage.getItem("userId");
    let isLoggedIn = sessionStorage.getItem("isLoggedIn") === "true";

    // this.amplifyService.auth().getCurrentUser();
    if (isLoggedIn && roleId !== Role.Client) {
      this._isSystemUser = true;
      this._isLoggedIn = true;
      this._user = this.recreateUserFromSession(+userId);
      this.configureUserAuthPool(dacConfig);
      
    }
    else if (clientId && isLoggedIn) {
      this._clientId = clientId;
      this._isLoggedIn = true;
      this._user = this.getClientUser(clientId);
    }
    else {
      this._user = this.getUnAuthenticatedUser();
    }

    this._loginStateSubject = new BehaviorSubject(this.getCurrentLoginState());
  }

  ngOnInit() {
    
  }

  getLoginState() : Observable<LoginStateNotification> {
    return this._loginStateSubject;
  }

  getCurrentUser() : Promise<User> {
    return Promise.resolve(this._user);
  }

  // TODO: stephen this should probably be put into ClientService now that we have
  // current user and the current user's username is the client token.
  getCurrentClient() : Promise<ManagedClient> {
    if (this._client) {
      return new Promise((resolve, reject) => {
        resolve(this._client);
      });
    }

    return this._clientService.getClient(this._clientId).then((client) => {
      this._client = client;
      return client;
    });
  }

  logout() {
    
    return this._dac$http.post("/logout",{}).then((resp) => {
      // since we'll be redirecting, we just want to clear the session storage.
      // TODO: stephen will this introduce bugs if the redirectUrl is within the same
      // application?

      // need to do our global logout here
      // By doing this, you are revoking all the auth tokens(id token, access token and refresh token)
      // which means the user is signed out from all the devices
      // Note: although the tokens are revoked, the AWS credentials will remain valid until they expire (which by default is 1 hour)
      this.amplifyService.auth().signOut({ global: true })
      .then(data => console.log(data))
      .catch(err => console.error(err));

      let result = resp.json() as any;
      if (result.redirectUrl) {
        sessionStorage.clear();
      }
      else {
        // clear and notify everyone we've logged out
        this.clearSession();
      }
      
      return result;
    });
  }

  clearSession() {
    sessionStorage.clear();
    this._client = null;
    this._clientId = null;
    this._isLoggedIn = false;
    this._isSystemUser = false;
    this._user = this.getUnAuthenticatedUser();
    this.assessmentService.clearCache();
    this._loginStateSubject.next(this.getCurrentLoginState());
  }

  authenticateClient(token:string) {
    sessionStorage.clear();
    let data = {
      clientID: token
    };
     return this._dac$http.post("/login/client", data).then((resp) => {
       this._clientId = token;
       this._isLoggedIn = true;
       // now populate the user here.
       this._user = this.getClientUser(token);
       this.updateSessionStorageWithUser(this._user);
       this._loginStateSubject.next(this.getCurrentLoginState());
      return token;
     })
     .catch((error) => {
       console.error(error);
       if (error.status === 404) {
         throw new Error("Invalid Token: " + token);
      }
      else {
        throw new Error("An error occurred communicating with the server.  Please try again.");
      }
     });
  }

  authenticateSSO(ssoRelayState:string) {
    return this._dac$http.securePost("sso", {ssoToken: ssoRelayState}, ssoRelayState)
    .then(resp => {
      let data = resp.json() as any;
      if (data.role == Role.Client) {
        this._clientId = data.id;
      }
      this.processUserAuthenticationFromResp(resp);
      return data;
    })
    .catch((error) => {
      this.alertService.error("Error is: " + JSON.stringify(error), 30000);
      if (error.redirectUrl) {
        window.location.href = error.redirectUrl;
      }
      else {
        // TODO: stephen need to navigate to an SSO error page if we have no redirect url.
      }
      console.error(error);
    })
  }

  authenticateCognito(user:any) {
    // send the amazon cognito user to the server to verify the user actually is valid
    // then we can setup the client session for the user...
    if (!(user && user.signInUserSession && user.signInUserSession.accessToken && user.signInUserSession.accessToken.jwtToken)) {
      throw new Error("There was a system error.  Please try again or contact support");
      // TODO: stephen seems like we want to be notified right away if the data format is off...
    }
    let authToken = user.signInUserSession.accessToken.jwtToken;
    return this._dac$http.post("/login", {authToken:authToken}).then(resp => this.processUserAuthenticationFromResp(resp))
    .catch((error) => {
      if (error.status === 401) {
        throw new Error("Your username or password was invalid");
      }
      else {
        throw new Error("There was an error communicating with the server.  Please try again or contact support");
      }
    })
  }

  migrateCognito(username:string) {
    return this._dac$http.post("/login/migrate", {username});
  }

  authenticate(username:string, password:string) {
    // TODO: stephen need a way of persisting the session storage
    // also need a better way of actually authenticating the clients.
    sessionStorage.clear();
    let data = {
      username: username
      ,password: password
    };
    return this._dac$http.post("/login", data).then(resp => this.processUserAuthenticationFromResp(resp))
    .catch((error) => {
      if (error.status === 401) {
        throw new Error("Your username or password was invalid");
      }
      else {
        throw new Error("There was an error communicating with the server.  Please try again or contact support");
      }
    })
  }

  public updateUserWithSubscription(subscription:Subscription) {
    this._user.subscription = subscription;
    this.updateSessionStorageWithUser(this._user);
    this._loginStateSubject.next(this.getCurrentLoginState());
  }

  private processUserAuthenticationFromResp(resp) {
    let user = resp.json() as any;
    Object.assign(this._user, user);
    if (user.subscription) {
      this._user.subscription = this.subHydrator.fromJSONObject(user.subscription);
    }
    this._isLoggedIn = true;
    this.updateSessionStorageWithUser(this._user);
    this._loginStateSubject.next(this.getCurrentLoginState());
    return resp;
  }

  private updateSessionStorageWithUser(user:User) {
    try {
      if (user.role === Role.UnAuthenticated) {
        sessionStorage.setItem("isLoggedIn", "false");
      }
      else {
        sessionStorage.setItem("isLoggedIn", "true");
        sessionStorage.setItem("roleId", user.role.toString());
        sessionStorage.setItem("username", user.username);
        sessionStorage.setItem("capabilities", JSON.stringify(user.capabilities));
        if (user.subscription) {
          sessionStorage.setItem("subscription", this.subHydrator.toJSON(user.subscription));
        }
        else {
          sessionStorage.removeItem("subscription");
        }

        if (user.role !== Role.Client) {
          sessionStorage.setItem("userId", user.id.toString());
          sessionStorage.setItem("companyId", user.companyID.toString());
        }
        else {
          sessionStorage.setItem("clientId", user.username);
        }
      }
    }
    catch (error) {
      console.error("SessionStorage quota exceeded or disabled", error);
    }
  }

  private configureUserAuthPool(dacConfig:DacConfig) {
    let configData = {

      // REQUIRED only for Federated Authentication - Amazon Cognito Identity Pool ID
      // identityPoolId: dacConfig.awsCognitoConfig.userPool.userIdentityPoolId, // 'us-east-1:0c4629df-3bcc-49b5-be6e-5861d5283b56',
      
      // REQUIRED - Amazon Cognito Region
      region: dacConfig.awsCognitoConfig.awsCognitoRegion, // 'us-east-1',

      // OPTIONAL - Amazon Cognito Federated Identity Pool Region 
      // Required only if it's different from Amazon Cognito Region
      // identityPoolRegion: 'XX-XXXX-X',

      // OPTIONAL - Amazon Cognito User Pool ID
      userPoolId: dacConfig.awsCognitoConfig.userPool.userPoolsId, // 'us-east-1_s9iDqPquj',

      // OPTIONAL - Amazon Cognito Web Client ID (26-char alphanumeric string)
      userPoolWebClientId: dacConfig.awsCognitoConfig.userPool.webClientId, //'1271f4r7a3f24kk53j6emll9nb',

      // OPTIONAL - Enforce user authentication prior to accessing AWS resources or not
      mandatorySignIn: false,

      // OPTIONAL - Configuration for cookie storage
      // cookieStorage: {
      // // REQUIRED - Cookie domain (only required if cookieStorage is provided)
      //     domain: '.yourdomain.com',
      // // OPTIONAL - Cookie path
      //     path: '/',
      // // OPTIONAL - Cookie expiration in days
      //     expires: 365,
      // // OPTIONAL - Cookie secure flag
      //     secure: true
      // },

      // OPTIONAL - customized storage object
      // storage: new MyStorage(),
      
      // OPTIONAL - Manually set the authentication flow type. Default is 'USER_SRP_AUTH'
      // authenticationFlowType: 'USER_PASSWORD_AUTH'
  };
  
    this._dac$http.currentStatus().subscribe((status) => {
      // if we need to authenticate.. 
      if (status == 401 && this._isLoggedIn && !this._inAuthentication) {
        // server has terminated the session... so we need to log ourselves out as well.
        this.amplifyService.auth().signOut({ global: true });
      }
    });
  }

  private recreateUserFromSession(userId:number) {
    let user = new User();
    user.id = +userId;
    user.role = +sessionStorage.getItem("roleId");
    user.username = sessionStorage.getItem("username");
    let companyId = sessionStorage.getItem("companyId");
    if (companyId) {
      user.companyID = +companyId;
    }
    let capString = sessionStorage.getItem("capabilities");
    if (capString) {
      let capabilities = [];
      try {
        capabilities = JSON.parse(capString);
      }
      catch (error) {
        console.error("Failed to parse capabilities string: ", error);
      }
      user.capabilities = capabilities;
    }

    let subJSONString = sessionStorage.getItem("subscription");
    if (subJSONString) {
      user.subscription = this.subHydrator.fromJSON(subJSONString);
    }

    // repopulate the information based upon what is retrieved.
    // TODO: right now only admins have access to this endpoint...
    // need to figure out why reg users can't call this.
    // this._dac$http.get("users/" + user.id).then((usr) => {
    //   user.username = usr.username;
    //   user.role = usr.role;
    //   user.companyID = usr.companyID;
    // });
    
    return user;
  }

  private createUserFromId(userId:number) {
    let user = new User();
    this._user.id = userId;
  }

  private getClientUser(clientId) {
    let user = new User();
    user.role = Role.Client;
    user.username = clientId;
    this.getCurrentClient().then((client) => {
      user.companyID = client.user.companyID;
    });
    return user;
  }

  private getUnAuthenticatedUser() {
    let user = new User();
    user.role = Role.UnAuthenticated;
    user.username = "unauthenticated";
    return user;
  }

  private getCurrentLoginState() : LoginStateNotification {
    return {
      role: this._user.role
      ,isLoggedIn: this._isLoggedIn
      ,clientId: this._clientId
      ,user:this._user
    };
  }

}



// WEBPACK FOOTER //
// ./src/app/login/services/auth.service.ts